'use client';

import React, { useState, useTransition } from 'react';
import Link from 'next/link';
import { Eye, EyeOff } from 'lucide-react';
import { repLoginAction, repForgotPasswordAction } from './actions';

const ROSE = '#C4688A';
const ROSE_RGB = '196,104,138';
const TEXT_DARK = '#1a1a2e';
const TEXT_MUTED = 'rgba(26,26,46,0.55)';
const PAGE_BG = '#FAFAF8';

interface RepLoginFormProps {
  initialError?: string;
  initialShowForgot?: boolean;
}

export default function RepLoginForm({ initialError, initialShowForgot }: RepLoginFormProps) {
  const [error, setError] = useState(initialError || '');
  const [isPending, startTransition] = useTransition();
  const [showPassword, setShowPassword] = useState(false);

  // Forgot password state
  const [showForgot, setShowForgot] = useState(initialShowForgot ?? false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotPending, startForgotTransition] = useTransition();
  const [forgotMessage, setForgotMessage] = useState('');
  // Pre-populate with the server-side error when the forgot panel opens on load
  const [forgotError, setForgotError] = useState(initialShowForgot ? (initialError ?? '') : '');

  const inputStyle = {
    background: 'white',
    border: `1.5px solid rgba(${ROSE_RGB},0.2)`,
    color: TEXT_DARK,
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = ROSE;
    e.target.style.boxShadow = `0 0 0 3px rgba(${ROSE_RGB},0.1)`;
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = `rgba(${ROSE_RGB},0.2)`;
    e.target.style.boxShadow = 'none';
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    const formData = new FormData(e.currentTarget);
    startTransition(async () => {
      const result = await repLoginAction(formData);
      if (result?.error) {
        setError(result.error);
      }
    });
  };

  const handleForgotSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setForgotError('');
    setForgotMessage('');
    const formData = new FormData(e.currentTarget);
    console.log('window.location.origin', window.location.origin);
    formData.set('origin', window.location.origin);
    console.log('formData', formData);
    startForgotTransition(async () => {
      const result = await repForgotPasswordAction(formData);
      if (result?.error) {
        setForgotError(result.error);
      } else {
        setForgotMessage('Check your email — we sent a password reset link.');
      }
    });
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-12"
      style={{ background: PAGE_BG, fontFamily: '"DM Sans", system-ui, sans-serif' }}
    >
      <div
        className="w-full max-w-md rounded-3xl p-8 md:p-10"
        style={{
          background: 'rgba(255,255,255,0.92)',
          border: `1px solid rgba(${ROSE_RGB},0.15)`,
          boxShadow: `0 8px 48px rgba(${ROSE_RGB},0.1), 0 2px 12px rgba(0,0,0,0.04)`,
        }}
      >
        {/* Logo / Brand */}
        <div className="flex flex-col items-center mb-8">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4"
            style={{ background: `rgba(${ROSE_RGB},0.12)`, border: `1px solid rgba(${ROSE_RGB},0.25)` }}
          >
            <span className="font-black text-xl" style={{ color: ROSE }}>✦</span>
          </div>
          <div className="flex items-center gap-2 mb-1">
            <div className="h-px w-6" style={{ background: `linear-gradient(to right, transparent, ${ROSE})` }} />
            <span className="text-[10px] font-bold tracking-[0.25em] uppercase" style={{ color: ROSE }}>
              Promodelle Agency
            </span>
            <div className="h-px w-6" style={{ background: `linear-gradient(to left, transparent, ${ROSE})` }} />
          </div>
          <h1 className="text-2xl font-black tracking-tight" style={{ color: TEXT_DARK }}>
            {showForgot ? 'Reset Password' : 'Rep Portal Login'}
          </h1>
          <p className="text-sm mt-1" style={{ color: TEXT_MUTED }}>
            {showForgot ? 'Enter your email to receive a reset link' : 'Sign in to access your dashboard'}
          </p>
        </div>

        {/* Forgot Password Form */}
        {showForgot ? (
          <div>
            {forgotMessage ? (
              <div
                className="px-4 py-3 rounded-xl text-sm mb-4"
                style={{ background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.25)', color: '#059669' }}
              >
                {forgotMessage}
              </div>
            ) : (
              <form onSubmit={handleForgotSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: TEXT_MUTED }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    placeholder="you@example.com"
                    autoComplete="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all"
                    style={inputStyle}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                  />
                </div>

                {forgotError && (
                  <div
                    className="px-4 py-3 rounded-xl text-sm"
                    style={{ background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.2)', color: '#dc2626' }}
                  >
                    {forgotError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={forgotPending}
                  className="w-full py-4 rounded-2xl font-black text-sm transition-all active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed mt-2"
                  style={{ background: ROSE, color: '#fff', boxShadow: `0 4px 24px rgba(${ROSE_RGB},0.3)` }}
                >
                  {forgotPending ? 'Sending…' : 'Send Reset Link →'}
                </button>
              </form>
            )}

            <div className="mt-5 text-center">
              <button
                type="button"
                onClick={() => { setShowForgot(false); setForgotMessage(''); setForgotError(''); }}
                className="text-xs font-semibold transition-colors"
                style={{ color: ROSE }}
              >
                ← Back to Sign In
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Sign In Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: TEXT_MUTED }}>
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="you@example.com"
                  autoComplete="email"
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all"
                  style={inputStyle}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: TEXT_MUTED }}>
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    placeholder="••••••••"
                    autoComplete="current-password"
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all"
                    style={{ ...inputStyle, paddingRight: '44px' }}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 transition-colors"
                    style={{ color: TEXT_MUTED }}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => setShowForgot(true)}
                    className="text-xs font-semibold transition-colors"
                    style={{ color: ROSE }}
                  >
                    Forgot password?
                  </button>
                </div>
              </div>

              {error && (
                <div
                  className="px-4 py-3 rounded-xl text-sm"
                  style={{ background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.2)', color: '#dc2626' }}
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isPending}
                className="w-full py-4 rounded-2xl font-black text-sm transition-all active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed mt-2"
                style={{ background: ROSE, color: '#fff', boxShadow: `0 4px 24px rgba(${ROSE_RGB},0.3)` }}
              >
                {isPending ? (
                  <span className="flex items-center justify-center gap-2">
                    <span
                      className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin"
                      style={{ borderColor: `rgba(255,255,255,0.4) transparent transparent transparent` }}
                    />
                    Signing In…
                  </span>
                ) : (
                  'Sign In →'
                )}
              </button>
            </form>

            {/* Footer links */}
            <div className="mt-6 flex flex-col items-center gap-2">
              <p className="text-xs" style={{ color: TEXT_MUTED }}>
                Not a rep yet?{' '}
                <Link href="/rep-landing" className="font-semibold transition-colors" style={{ color: ROSE }}>
                  Apply here
                </Link>
              </p>
              <Link href="/" className="text-xs transition-colors" style={{ color: TEXT_MUTED }}>
                ← Back to home
              </Link>
            </div>
          </>
        )}
      </div>

      <p className="mt-8 text-[10px] font-bold tracking-[0.22em] uppercase" style={{ color: 'rgba(150,130,140,0.4)' }}>
        FindACut.com
      </p>
    </div>
  );
}
